<template>
  <div class="hl-text-input-container">
    <div class="flex space-x-3">
      <span v-if="label" :for="name" class="hl-text-input-label block text-sm font-medium text-gray-700 mb-1">{{
        label
      }}</span>
      <span
        v-if="tooltip"
        class="input-group-addon"
        v-tooltip.hover
        :title="tooltip"
      >
        <i class="fas fa-question-circle"></i>
      </span>
    </div>
    <div class="relative rounded-md shadow-sm" :class="{'hl-text-input-wrapper': adjustAddOns}">
      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <UIIcon v-if="icon" :icon="icon" />
      </div>
      <div class="absolute inset-y-0 left-0 mx-2 flex items-center pointer-events-none hl-text-input-addon">
        <span v-if="preAddOn" class="text-gray-500 sm:text-sm">
          {{ preAddOn }}
        </span>
      </div>
      <UITextInput
        ref="inputRef"
        :type="showPasswordToggle ? (showPassword ? 'text' : 'password') : type"
        :name="name"
        :id="id"
        :placeholder="placeholder"
        @update:modelValue="handleInput"
        @blur="handleBlur"
        @change="handleChange"
        :modelValue="inputValue || modelValue"
        :min="min"
        :disabled="!!disabled"
        :autocomplete="autocomplete"
        v-bind:class="{'pl-10': icon, 'pl-4': preAddOn, 'arrow-disable': disableArrow, 'border-red-300 focus:ring-red-400 focus:border-red-400 pr-10' : error || fieldError}"
        @enter="val => $emit('enter', val)"
        @focus="val => $emit('focus', val)"
        :readonly="readonly"
        :maxlength="maxlength"
        :ignoreLastPass="ignoreLastPass"
      />
      <div v-if="error || fieldError" class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
        <i class="fas fa-exclamation-circle h-5 w-5 text-red-400 text-lg mb-2 mr-1 pl-1" aria-hidden="true"></i>
      </div>
      <!-- Password toggle icon - shows when no error and showPasswordToggle is true -->
      <button
        v-else-if="showPasswordToggle"
        type="button"
        class="absolute inset-y-0 right-0 pr-3 flex items-center justify-center bg-transparent border-none cursor-pointer"
        @click="showPassword = !showPassword"
        :aria-label="passwordToggleAriaLabel"
      >
        <img v-if="!showPassword" :src="eyeSlashIconUrl" alt="" class="w-4 h-3 block flex-shrink-0 object-contain" />
        <img v-else :src="eyeIconUrl" alt="" class="w-4 h-3 block flex-shrink-0 object-contain" />
      </button>
     <div class="absolute inset-y-0 right-0 flex items-center pointer-events-none hl-text-input-addon" :class="(error || fieldError) ? 'pr-10' : 'pr-3'">
      <span v-if="postAddOn" class="text-gray-500 sm:text-sm">
        {{ postAddOn }}
      </span>
     </div>
    </div>
   <p v-if="description" class="hl-input-description mt-2 text-xs text-gray-500">{{description}}</p>
   <p v-if="(errorMsg && error) || fieldError" class="hl-input-error mt-2 text-sm text-red-600" id="email-error">{{errorMsg || fieldError}}</p>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n'
import UITextInput from './UITextInput.vue'
import eyeIconUrl from '@/assets/pmd/img/icon-eye.svg?url'
import eyeSlashIconUrl from '@/assets/pmd/img/icon-eye-slash.svg?url'

export default defineComponent({
  components: { UITextInput },
  emits: ['keyup', 'enter', 'focus', 'blur', 'change', 'update:modelValue'],
  props: {
    modelValue: {
      type:[String, Number],
      default:''
    },
    label: {
      type:String
    },
    id: {
      type:String,
    },
    placeholder: {
      type:String,
      default:''
    },
    disabled: {
      type: [Boolean, String],
      default: false
    },
    name: {
      type:String,
      default:''
    },
    type: {
      type:String,
      default:'text'
    },
    error: {
      type: Boolean,
      default: false
    },
    errorMsg: {
      type: String,
      default: ''
    },
    icon: {
      type: String
    },
    description:{
      type: String,
      default: ''
    },
    min: {
      type: [Number, String]
    },
    autocomplete: {
      type: String,
      default: ''
    },
    readonly: {
      type: [Boolean, String]
    },
    maxlength: {
      type: String,
      default: ''
    },
    ignoreLastPass:{
      type: Boolean,
      default: true
    },
    tooltip: {
      type: String,
    },
    postAddOn: {
      type: String,
      default: ''
    },
    preAddOn: {
      type: String,
      default: ''
    },
    disableArrow: {
      type: Boolean,
      default: false
    },
    adjustAddOns: {
      type: Boolean,
      default: false
    },
    showPasswordToggle: {
      type: Boolean,
      default: false
    },
    //vee-validate rules
    rules: {
      type: [String, Object],
      default: ''
    },
    //vee-validate fieldOptions
    fieldOptions: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    modelValue(newVal) {
      this.inputValue = newVal
    }
  },
  setup(props, { emit }) {
      // Create refs for reactive state
      const inputValue = ref(props.modelValue);
    const fieldError = ref('');
    const showPassword = ref(false);
    const { t } = useI18n();

    const passwordToggleAriaLabel = computed(() => {
      return showPassword.value ? t('common.hidePassword') : t('common.showPassword');
    });

    //event contains the actual value of the input
    let handleBlur = (e: any) => emit('blur', e);
    let handleChange = (e: any) => emit('change', e);
    let handleInput = (e: any) => emit('update:modelValue', e);

    // Setup vee-validate integration if name prop is provided
    if (props.name) {
      const { value, errorMessage, handleBlur: veeBlur, handleChange: veeChange } = useField(props.name, props.rules || '',
      {
        initialValue: props.modelValue,
        ...props.fieldOptions
      });

      // Create more proactive validation handlers
      handleInput = (event) => {
        veeChange(event); // Trigger validation immediately
        emit('update:modelValue', event);
      };

      handleBlur = (event) => {
        veeBlur(event);
        emit('blur', event);
      };

      handleChange = (event) => {
        veeChange(event);
        emit('change', event);
      };

      // Update our reactive refs when validation state changes
      watch(value, (newVal) => {
        inputValue.value = newVal;
      });

      watch(errorMessage, (newVal) => {
        fieldError.value = newVal || '';
      });

      // Set initial values
      inputValue.value = value.value;
      fieldError.value = errorMessage.value || '';
    }

    return {
      eyeIconUrl,
      eyeSlashIconUrl,
      inputValue,
      fieldError,
      showPassword,
      passwordToggleAriaLabel,
      handleBlur,
      handleChange,
      handleInput
    };
  }
})
</script>

<style scoped>
  .arrow-disable::-webkit-inner-spin-button,
  .arrow-disable::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .hl-text-input-wrapper {
    border: 1px solid rgb(209, 213, 219);
    border-radius: 0.25rem;
    display: flex;
  }
  .hl-text-input-wrapper .hl-text-input {
    border: none;
    padding-left: 0 !important;
    flex: 1;
  }
  .hl-text-input-wrapper .hl-text-input:focus {
    outline: none;
    border: none;
    box-shadow: none;
  }
  .hl-text-input-wrapper .hl-text-input-addon {
    position: relative;
  }
  .create-wp-pwd #email-error {
    position: absolute;
  }
</style>
